<template>
  <div class="cf-report" :style="themeVars">
    <div v-if="summary" class="cf-summary">
      <div class="cf-summary__item">
        <div class="cf-summary__label">Total Uang Masuk</div>
        <div class="cf-summary__value">{{ formatAmount(summary.total_uang_masuk) }}</div>
      </div>
      <div class="cf-summary__item">
        <div class="cf-summary__label">Total Uang Keluar</div>
        <div class="cf-summary__value">{{ formatAmount(summary.total_uang_keluar) }}</div>
      </div>
      <div class="cf-summary__item cf-summary__item--net">
        <div class="cf-summary__label">Net Cashflow</div>
        <div class="cf-summary__value">{{ formatAmount(summary.net_cashflow) }}</div>
      </div>
    </div>

    <div class="cf-scroll">
      <table class="cf-table">
        <thead>
          <tr>
            <th class="cf-th cf-sticky cf-sticky--1 cf-col-coa">No. COA</th>
            <th class="cf-th cf-sticky cf-sticky--2 cf-col-name">Nama COA</th>
            <th
              v-for="header in headers"
              :key="headerId(header)"
              class="cf-th cf-col-sumber cf-th--tooltip"
              :data-tooltip="header.cashflow_sumber_coa_name">
              <span class="cf-th__sumber">{{ header.cashflow_sumber_code }}</span>
            </th>
            <th class="cf-th cf-col-total">Grand Total</th>
            <th class="cf-th cf-col-trx">Jumlah Transaksi</th>
          </tr>
        </thead>

        <tbody>
          <tr
            v-for="row in tableRows"
            :key="row.id"
            :class="rowClass(row)">
            <!-- Category / Type section labels -->
            <template v-if="row.kind === 'category' || row.kind === 'type'">
              <td
                class="cf-td cf-sticky cf-sticky--1 cf-col-coa cf-td--section"
                :colspan="2">
                {{ row.label }}
              </td>
              <td
                v-for="header in headers"
                :key="`${row.id}-${headerId(header)}`"
                class="cf-td cf-col-sumber">
              </td>
              <td class="cf-td cf-col-total"></td>
              <td class="cf-td cf-col-trx"></td>
            </template>

            <!-- Detail -->
            <template v-else-if="row.kind === 'detail'">
              <td class="cf-td cf-sticky cf-sticky--1 cf-col-coa">{{ row.coa_number }}</td>
              <td class="cf-td cf-sticky cf-sticky--2 cf-col-name">{{ row.coa_name }}</td>
              <td
                v-for="header in headers"
                :key="`${row.id}-${headerId(header)}`"
                class="cf-td cf-col-sumber cf-num"
                :class="{ 'cf-num--zero': !sumberNominal(row, header) }">
                {{ formatAmount(sumberNominal(row, header)) }}
              </td>
              <td class="cf-td cf-col-total cf-num cf-num--bold">
                {{ formatAmount(row.grand_total) }}
              </td>
              <td class="cf-td cf-col-trx cf-num">
                {{ formatAmount(row.total_transaksi) }}
              </td>
            </template>

            <!-- Subtotal / Category Total -->
            <template v-else-if="row.kind === 'subtotal' || row.kind === 'category_total'">
              <td
                class="cf-td cf-sticky cf-sticky--1 cf-col-coa cf-td--label"
                :class="{ 'cf-td--label-wrap': row.wrap_label }"
                :colspan="2">
                {{ row.label }}
              </td>
              <td
                v-for="header in headers"
                :key="`${row.id}-${headerId(header)}`"
                class="cf-td cf-col-sumber cf-num"
                :class="{ 'cf-num--zero': row.values && !footerNominal(row, header) }">
                <template v-if="row.values">
                  {{ formatAmount(footerNominal(row, header)) }}
                </template>
              </td>
              <td class="cf-td cf-col-total cf-num cf-num--bold">
                {{ formatAmount(row.grand_total) }}
              </td>
              <td class="cf-td cf-col-trx cf-num cf-num--bold">
                {{ formatAmount(row.total_transaksi) }}
              </td>
            </template>

            <!-- Footer by sumber -->
            <template v-else-if="row.kind === 'footer' || row.kind === 'footer_total'">
              <td
                class="cf-td cf-sticky cf-sticky--1 cf-col-coa cf-td--label"
                :colspan="2">
                {{ row.label }}
              </td>
              <td
                v-for="header in headers"
                :key="`${row.id}-${headerId(header)}`"
                class="cf-td cf-col-sumber cf-num"
                :class="{ 'cf-num--zero': !footerNominal(row, header) }">
                {{ formatAmount(footerNominal(row, header)) }}
              </td>
              <td class="cf-td cf-col-total cf-num cf-num--bold">
                {{ formatAmount(row.grand_total) }}
              </td>
              <td class="cf-td cf-col-trx cf-num cf-num--bold">
                {{ formatAmount(row.total_transaksi) }}
              </td>
            </template>
          </tr>

          <tr v-if="!tableRows.length">
            <td
              class="cf-td cf-empty"
              :colspan="emptyColspan">
              Belum Ada Data Laporan Arus Kas
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="sumberDanaSummary" class="cf-sumber-summary">
      <div class="cf-sumber-summary__title">Summary</div>

      <v-divider class="my-2"></v-divider>

      <div class="cf-sumber-summary__group">
        <div class="cf-sumber-summary__heading">Uang Masuk</div>
        <div class="cf-sumber-summary__row">
          <span class="cf-sumber-summary__label">Total Uang Masuk di Sumber Dana Kas Kecil</span>
          <span class="cf-sumber-summary__value">{{ formatAmount(sumberDanaSummary.uang_masuk.kas.total_nominal) }}</span>
          <!-- <span class="cf-sumber-summary__trx">{{ formatAmount(sumberDanaSummary.uang_masuk.kas.total_transaksi) }} trx</span> -->
        </div>
        <div class="cf-sumber-summary__row">
          <span class="cf-sumber-summary__label">Total Uang Masuk di Sumber Dana Bank</span>
          <span class="cf-sumber-summary__value">{{ formatAmount(sumberDanaSummary.uang_masuk.bank.total_nominal) }}</span>
          <!-- <span class="cf-sumber-summary__trx">{{ formatAmount(sumberDanaSummary.uang_masuk.bank.total_transaksi) }} trx</span> -->
        </div>
        <div class="cf-sumber-summary__row cf-sumber-summary__row--total">
          <span class="cf-sumber-summary__label">Total Uang Masuk Keseluruhan</span>
          <span class="cf-sumber-summary__value">{{ formatAmount(sumberDanaSummary.uang_masuk.total.total_nominal) }}</span>
          <!-- <span class="cf-sumber-summary__trx">{{ formatAmount(sumberDanaSummary.uang_masuk.total.total_transaksi) }} trx</span> -->
        </div>
      </div>

      <v-divider class="my-2"></v-divider>

      <div class="cf-sumber-summary__group">
        <div class="cf-sumber-summary__heading">Uang Keluar</div>
        <div class="cf-sumber-summary__row">
          <span class="cf-sumber-summary__label">Total Uang Keluar di Sumber Dana Kas Kecil</span>
          <span class="cf-sumber-summary__value">{{ formatAmount(sumberDanaSummary.uang_keluar.kas.total_nominal) }}</span>
          <!-- <span class="cf-sumber-summary__trx">{{ formatAmount(sumberDanaSummary.uang_keluar.kas.total_transaksi) }} trx</span> -->
        </div>
        <div class="cf-sumber-summary__row">
          <span class="cf-sumber-summary__label">Total Uang Keluar di Sumber Dana Bank</span>
          <span class="cf-sumber-summary__value">{{ formatAmount(sumberDanaSummary.uang_keluar.bank.total_nominal) }}</span>
          <!-- <span class="cf-sumber-summary__trx">{{ formatAmount(sumberDanaSummary.uang_keluar.bank.total_transaksi) }} trx</span> -->
        </div>
        <div class="cf-sumber-summary__row cf-sumber-summary__row--total pb-4">
          <span class="cf-sumber-summary__label">Total Uang Keluar Keseluruhan</span>
          <span class="cf-sumber-summary__value">{{ formatAmount(sumberDanaSummary.uang_keluar.total.total_nominal) }}</span>
          <!-- <span class="cf-sumber-summary__trx">{{ formatAmount(sumberDanaSummary.uang_keluar.total.total_transaksi) }} trx</span> -->
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import {
    buildTableRows,
    buildFooterRows,
    buildSumberDanaSummary,
    formatAmount,
    headerLabel,
    headerId,
    getSumberNominal,
    footerValueForHeader
  } from '@/utils/cashflowReport'

  const STICKY_COA_WIDTH = 110
  const STICKY_NAME_WIDTH = 220

  export default {
    name: 'CashflowReportTable',
    props: {
      rekapitulasi: {
        type: Object,
        default: null
      },
      summary: {
        type: Object,
        default: null
      },
      color: {
        type: String,
        default: '#1976D2'
      }
    },
    computed: {
      headers () {
        const list = (this.rekapitulasi && this.rekapitulasi.header_sumber_dana_array) || []
        return [...list].sort((a, b) => this.headerSortValue(a) - this.headerSortValue(b))
      },
      tableRows () {
        if (!this.rekapitulasi) return []
        const body = buildTableRows(this.rekapitulasi.group_array)
        const footer = buildFooterRows(this.rekapitulasi)
        return body.concat(footer)
      },
      sumberDanaSummary () {
        return buildSumberDanaSummary(this.rekapitulasi)
      },
      emptyColspan () {
        return 2 + this.headers.length + 2
      },
      themeVars () {
        return {
          '--cf-accent': this.color || '#1976D2',
          '--cf-sticky-1': `${STICKY_COA_WIDTH}px`,
          '--cf-sticky-2-left': `${STICKY_COA_WIDTH}px`,
          '--cf-sticky-2': `${STICKY_NAME_WIDTH}px`
        }
      }
    },
    methods: {
      formatAmount,
      headerLabel,
      headerId,
      headerSortValue (header) {
        const code = (header && header.cashflow_sumber_code) || ''
        const match = code.match(/(\d+)/)

        return match ? parseInt(match[1], 10) : Number.MAX_SAFE_INTEGER
      },
      rowClass (row) {
        return [
          `cf-row--${row.kind}`,
          row.type_key ? `cf-row--${row.type_key}` : null
        ]
      },
      sumberNominal (row, header) {
        return getSumberNominal(
          row.nominal_by_sumber_array,
          headerId(header)
        ).total_nominal
      },
      footerNominal (row, header) {
        return footerValueForHeader(row, headerId(header)).total_nominal
      }
    }
  }
</script>

<style scoped>
  .cf-report {
    --cf-bg: #ffffff;
    --cf-bg-muted: #f7f8fa;
    --cf-bg-head: var(--cf-accent);
    --cf-bg-category: #e8eef5;
    --cf-bg-type: #f3f5f8;
    --cf-bg-subtotal: #eef6ee;
    --cf-bg-cat-total: #e3edf7;
    --cf-bg-footer: #f5f0e6;
    --cf-bg-footer-total: #e8e4dc;
    --cf-border: #e0e3e8;
    --cf-text: #1f2933;
    --cf-text-muted: #6b7280;
    --cf-head-text: #ffffff;
    --cf-sticky-bg: #ffffff;
    --cf-hover: #f0f4fa;
    --cf-zero: #9aa3ad;

    color: var(--cf-text);
    background: var(--cf-bg);
  }

  /* @media (prefers-color-scheme: dark) {
    .cf-report {
      --cf-bg: #1a1d23;
      --cf-bg-muted: #22262e;
      --cf-bg-category: #2a3340;
      --cf-bg-type: #252b34;
      --cf-bg-subtotal: #1e2e24;
      --cf-bg-cat-total: #1e2a38;
      --cf-bg-footer: #2e2a22;
      --cf-bg-footer-total: #363024;
      --cf-border: #3a404a;
      --cf-text: #e8eaed;
      --cf-text-muted: #9aa3ad;
      --cf-sticky-bg: #1a1d23;
      --cf-hover: #252b34;
      --cf-zero: #6b7280;
    }
  }

  .theme--dark .cf-report {
    --cf-bg: #1a1d23;
    --cf-bg-muted: #22262e;
    --cf-bg-category: #2a3340;
    --cf-bg-type: #252b34;
    --cf-bg-subtotal: #1e2e24;
    --cf-bg-cat-total: #1e2a38;
    --cf-bg-footer: #2e2a22;
    --cf-bg-footer-total: #363024;
    --cf-border: #3a404a;
    --cf-text: #e8eaed;
    --cf-text-muted: #9aa3ad;
    --cf-sticky-bg: #1a1d23;
    --cf-hover: #252b34;
    --cf-zero: #6b7280;
  } */

  .cf-summary {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 12px;
  }

  .cf-summary__item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 4px 8px;
    border-radius: 6px;
    background: var(--cf-bg-muted);
    border: 1px solid var(--cf-border);
    min-width: 180px;
  }

  .cf-summary__item--net {
    background: var(--cf-bg-cat-total);
    border-color: #c9d7e6;
    color: #1976D2;
  }

  .cf-summary__label {
    font-size: 11px;
    font-weight: 600;
    color: var(--cf-text-muted);
  }

  .cf-summary__value {
    font-size: 13px;
    font-weight: 800;
    color: var(--cf-text);
  }

  .cf-sumber-summary {
    margin-top: 16px;
    /* padding: 8px; */
    border: 1px solid var(--cf-border);
    width: 350px;
  }

  .cf-sumber-summary__title {
    font-size: 12px;
    font-weight: 700;
    color: var(--cf-text);
    margin-bottom: 10px;
    letter-spacing: 0.02em;
    padding: 8px 8px 0 8px;
  }

  .cf-sumber-summary__group {
    margin-bottom: 12px;
    padding: 0 8px 0 8px;
    padding-bottom: 0;
  }

  .cf-sumber-summary__group:last-child {
    margin-bottom: 0;
  }

  .cf-sumber-summary__heading {
    font-size: 11px;
    font-weight: 700;
    /* color: var(--cf-text-muted); */
    margin-bottom: 6px;
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }

  .cf-sumber-summary__row {
    display: grid;
    grid-template-columns: 1fr auto auto;
    gap: 12px;
    align-items: baseline;
    padding: 4px 0;
    font-size: 11px;
    line-height: 1.4;
    border-bottom: 1px solid #f0f0f0;
  }

  .cf-sumber-summary__row:last-child {
    border-bottom: none;
  }

  .cf-sumber-summary__row--total {
    font-weight: 700;
    /* border-top: 1px solid var(--cf-border); */
    margin-top: 2px;
    padding-top: 6px;
  }

  .cf-sumber-summary__label {
    color: var(--cf-text);
    min-width: 0;
    word-break: break-word;
  }

  .cf-sumber-summary__value {
    font-variant-numeric: tabular-nums;
    font-weight: 600;
    text-align: right;
    white-space: nowrap;
  }

  .cf-sumber-summary__trx {
    font-variant-numeric: tabular-nums;
    color: var(--cf-text-muted);
    text-align: right;
    white-space: nowrap;
    min-width: 48px;
  }

  .cf-meta__sep {
    text-align: center;
  }

  .cf-meta__value {
    font-variant-numeric: tabular-nums;
    text-align: right;
  }

  /* —— Scroll shell —— */
  .cf-scroll {
    overflow: auto;
    max-height: calc(100vh - 260px);
    border: 1px solid var(--cf-border);
    border-radius: 10px;
    background: var(--cf-bg);
    -webkit-overflow-scrolling: touch;
  }

  .cf-table {
    border-collapse: separate;
    border-spacing: 0;
    width: max-content;
    min-width: 100%;
    font-size: 11px;
    line-height: 1.35;
  }

  .cf-th,
  .cf-td {
    border-bottom: 1px solid var(--cf-border);
    border-right: 1px solid var(--cf-border);
    padding: 6px 8px;
    vertical-align: middle;
    white-space: nowrap;
  }

  .cf-th {
    position: sticky;
    top: 0;
    z-index: 4;
    background: var(--cf-bg-head) !important;
    color: var(--cf-head-text);
    font-weight: 600;
    font-size: 10px;
    text-align: center;
    height: 42px;
  }

  .cf-th.cf-sticky {
    z-index: 6;
  }

  .cf-th__sumber {
    display: block;
    max-width: 128px;
    white-space: normal;
    line-height: 1.25;
    text-align: center;
    margin: 0 auto;
  }

  .cf-th--tooltip::after {
    content: attr(data-tooltip);
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translate(-50%, 6px);
    background: #24272b;
    color: #fff;
    padding: 5px 9px;
    border-radius: 5px;
    font-size: 11px;
    font-weight: 400;
    text-transform: none;
    white-space: nowrap;
    pointer-events: none;
    opacity: 0;
    visibility: hidden;
    transition: opacity 0.08s ease-out;
    z-index: 20;
  }

  .cf-th--tooltip:hover::after {
    opacity: 1;
    visibility: visible;
  }

  .cf-sticky {
    position: sticky;
    background: var(--cf-sticky-bg);
  }

  .cf-sticky--1 {
    left: 0;
    z-index: 3;
    min-width: var(--cf-sticky-1);
    max-width: var(--cf-sticky-1);
    width: var(--cf-sticky-1);
  }

  .cf-sticky--2 {
    left: var(--cf-sticky-2-left);
    z-index: 3;
    min-width: var(--cf-sticky-2);
    max-width: var(--cf-sticky-2);
    width: var(--cf-sticky-2);
  }

  .cf-th.cf-sticky--1,
  .cf-th.cf-sticky--2 {
    background: var(--cf-bg-head) !important;
  }

  .cf-col-sumber {
    min-width: 128px;
    text-align: right;
  }

  .cf-col-total,
  .cf-col-trx {
    min-width: 120px;
    text-align: right;
  }

  .cf-col-name {
    white-space: normal;
    min-width: var(--cf-sticky-2);
    max-width: var(--cf-sticky-2);
  }

  .cf-num {
    font-variant-numeric: tabular-nums;
    text-align: right;
  }

  .cf-num--bold {
    font-weight: 700;
  }

  .cf-num--zero {
    color: var(--cf-zero);
  }

  .cf-td--section,
  .cf-td--label {
    font-weight: 700;
    white-space: nowrap;
  }

  .cf-td--label-wrap {
    white-space: normal !important;
    overflow-wrap: anywhere;
    word-break: break-word;
    line-height: 1.35;
    height: auto !important;
    min-height: 26px;
    padding-top: 6px;
    padding-bottom: 6px;
    vertical-align: middle;
  }

  .cf-row--subtotal .cf-td--label-wrap {
    text-align: left;
  }

  .cf-td--section[colspan="2"],
  .cf-td--label[colspan="2"] {
    left: 0;
    z-index: 3;
    min-width: calc(var(--cf-sticky-1) + var(--cf-sticky-2));
    max-width: calc(var(--cf-sticky-1) + var(--cf-sticky-2));
    width: calc(var(--cf-sticky-1) + var(--cf-sticky-2));
  }

  .cf-empty {
    text-align: center;
    padding: 48px 16px;
    color: var(--cf-text-muted);
    font-size: 14px;
  }

  .cf-row--category .cf-td {
    background: var(--cf-bg-category);
  }

  .cf-row--category:first-child .cf-td {
    border-top: none;
  }

  .cf-row--category:not(:first-child) .cf-td {
    border-top: 25px solid var(--cf-bg);
  }

  .cf-row--type .cf-td {
    background: var(--cf-bg-type);
  }

  .cf-row--subtotal .cf-td {
    background: var(--cf-bg-subtotal);
  }

  .cf-row--category_total .cf-td {
    background: var(--cf-bg-cat-total);
  }

  .cf-row--footer .cf-td {
    background: var(--cf-bg-footer);
  }

  .cf-row--footer_total .cf-td {
    background: var(--cf-bg-footer-total);
  }

  .cf-row--detail .cf-sticky {
    background: var(--cf-sticky-bg);
  }

  .cf-row--detail:hover .cf-td {
    background: var(--cf-hover);
  }

  .cf-row--category .cf-sticky,
  .cf-row--type .cf-sticky,
  .cf-row--subtotal .cf-sticky,
  .cf-row--category_total .cf-sticky,
  .cf-row--footer .cf-sticky,
  .cf-row--footer_total .cf-sticky,
  .cf-row--detail:hover .cf-sticky {
    background: inherit;
  }

  .cf-row--category .cf-sticky { background: var(--cf-bg-category); }
  .cf-row--type .cf-sticky { background: var(--cf-bg-type); }
  .cf-row--subtotal .cf-sticky { background: var(--cf-bg-subtotal); }
  .cf-row--category_total .cf-sticky { background: var(--cf-bg-cat-total); }
  .cf-row--footer .cf-sticky { background: var(--cf-bg-footer); }
  .cf-row--footer_total .cf-sticky { background: var(--cf-bg-footer-total); }
  .cf-row--detail:hover .cf-sticky { background: var(--cf-hover); }

  .cf-row--category .cf-td--section {
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0.01em;
    padding-top: 10px;
    padding-bottom: 10px;
    text-transform: uppercase;
  }

  .cf-sticky--1 {
    box-shadow: 1px 0 0 var(--cf-border);
  }

  .cf-sticky--2 {
    box-shadow: 2px 0 4px rgba(0, 0, 0, 0.06);
  }

  .cf-td--section[colspan="2"],
  .cf-td--label[colspan="2"] {
    box-shadow: 2px 0 4px rgba(0, 0, 0, 0.06);
  }

  @media (max-width: 960px) {
    .cf-scroll {
      max-height: calc(100vh - 320px);
    }

    .cf-summary__item {
      min-width: calc(50% - 8px);
      flex: 1 1 calc(50% - 8px);
    }
  }

  @media (max-width: 600px) {
    .cf-summary__item {
      min-width: 100%;
      flex: 1 1 100%;
    }

    .cf-table {
      font-size: 10px;
    }

    .cf-col-sumber {
      min-width: 110px;
    }
  }
</style>